import { useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  vehicleService,
  driverService,
  type VehicleUnavailabilityDto,
  type DriverUnavailabilityDto,
  type VehicleUnavailabilityWriteRequest,
  type UnavailabilityType,
  type DayOfWeek,
} from '../api/adminServices'
import { useToast } from './useToast'
import { fmtDate, fmtDateTime } from '../utils/propertyFormat'

type AnyUnavailabilityDto = VehicleUnavailabilityDto | DriverUnavailabilityDto

interface Props {
  /** Either provide vehicleId, or driverId — exactly one. */
  vehicleId?: string
  driverId?: string
}

const TYPE_OPTIONS: { value: UnavailabilityType; label: string }[] = [
  { value: 'ONE_TIME', label: 'One-time period' },
  { value: 'DATE_RANGE', label: 'Date range' },
  { value: 'RECURRING_WEEKLY', label: 'Recurring weekly' },
  { value: 'RECURRING_DAILY', label: 'Recurring daily' },
]

const DAY_OPTIONS: DayOfWeek[] = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
]

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1)
}

function endOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0)
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

function dayOfWeekFromDate(d: Date): DayOfWeek {
  const map: DayOfWeek[] = [
    'SUNDAY',
    'MONDAY',
    'TUESDAY',
    'WEDNESDAY',
    'THURSDAY',
    'FRIDAY',
    'SATURDAY',
  ]
  return map[d.getDay()]
}

function affectsDay(u: AnyUnavailabilityDto, day: Date): boolean {
  switch (u.unavailabilityType) {
    case 'ONE_TIME':
    case 'ASSIGNED': {
      if (!u.startDateTime || !u.endDateTime) return false
      const s = new Date(u.startDateTime)
      const e = new Date(u.endDateTime)
      const dayStart = new Date(day.getFullYear(), day.getMonth(), day.getDate())
      const dayEnd = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 23, 59, 59)
      return s <= dayEnd && e >= dayStart
    }
    case 'DATE_RANGE': {
      if (!u.startDate || !u.endDate) return false
      const s = new Date(u.startDate)
      const e = new Date(u.endDate)
      const dayDate = new Date(day.getFullYear(), day.getMonth(), day.getDate())
      return dayDate >= s && dayDate <= e
    }
    case 'RECURRING_WEEKLY':
      return u.dayOfWeek === dayOfWeekFromDate(day)
    case 'RECURRING_DAILY':
      return true
    default:
      return false
  }
}

function describeUnavailability(u: AnyUnavailabilityDto): string {
  switch (u.unavailabilityType) {
    case 'ONE_TIME':
    case 'ASSIGNED':
      return `${fmtDateTime(u.startDateTime)} → ${fmtDateTime(u.endDateTime)}`
    case 'DATE_RANGE':
      return `${fmtDate(u.startDate)} → ${fmtDate(u.endDate)}`
    case 'RECURRING_WEEKLY':
      return `Every ${u.dayOfWeek?.toLowerCase()}${u.startTimeOfDay ? ` ${u.startTimeOfDay}` : ''}${u.endTimeOfDay ? `–${u.endTimeOfDay}` : ''}`
    case 'RECURRING_DAILY':
      return `Daily${u.dailyStartTime ? ` ${u.dailyStartTime}` : ''}${u.dailyEndTime ? `–${u.dailyEndTime}` : ''}`
    default:
      return u.unavailabilityType
  }
}

function emptyForm(): VehicleUnavailabilityWriteRequest {
  return {
    unavailabilityType: 'ONE_TIME',
    startDateTime: null,
    endDateTime: null,
    startDate: null,
    endDate: null,
    dayOfWeek: null,
    startTimeOfDay: null,
    endTimeOfDay: null,
    dailyStartTime: null,
    dailyEndTime: null,
    reason: null,
  }
}

export default function VehicleUnavailabilityCalendar({ vehicleId, driverId }: Props) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [month, setMonth] = useState(startOfMonth(new Date()))
  const [formOpen, setFormOpen] = useState(false)
  const [form, setForm] = useState<VehicleUnavailabilityWriteRequest>(emptyForm())

  const subjectKind: 'vehicle' | 'driver' = driverId ? 'driver' : 'vehicle'
  const subjectId = driverId ?? vehicleId ?? ''
  const queryKey = [`${subjectKind}-unavailability`, subjectId] as const

  const { data, isLoading, error } = useQuery<AnyUnavailabilityDto[]>({
    queryKey,
    queryFn: () =>
      subjectKind === 'driver'
        ? driverService.getUnavailability(subjectId)
        : vehicleService.getUnavailability(subjectId),
    enabled: !!subjectId,
  })

  const addMutation = useMutation<AnyUnavailabilityDto, Error, VehicleUnavailabilityWriteRequest>({
    mutationFn: (req: VehicleUnavailabilityWriteRequest) =>
      subjectKind === 'driver'
        ? driverService.addUnavailability(subjectId, req)
        : vehicleService.addUnavailability(subjectId, req),
    onSuccess: () => {
      toast.show('Unavailability added', 'success')
      queryClient.invalidateQueries({ queryKey })
      setFormOpen(false)
      setForm(emptyForm())
    },
    onError: (e: Error) => toast.show(`Failed to add: ${e.message}`, 'error'),
  })

  const deleteMutation = useMutation({
    mutationFn: (unavailabilityId: string) =>
      subjectKind === 'driver'
        ? driverService.removeUnavailability(unavailabilityId)
        : vehicleService.removeUnavailability(subjectId, unavailabilityId),
    onSuccess: () => {
      toast.show('Unavailability removed', 'success')
      queryClient.invalidateQueries({ queryKey })
    },
    onError: (e: Error) => toast.show(`Failed to remove: ${e.message}`, 'error'),
  })

  const unavailabilities = data ?? []

  const days = useMemo(() => {
    const first = startOfMonth(month)
    const last = endOfMonth(month)
    const startWeekday = (first.getDay() + 6) % 7 // Monday-first grid
    const cells: { date: Date | null }[] = []
    for (let i = 0; i < startWeekday; i++) cells.push({ date: null })
    for (let d = 1; d <= last.getDate(); d++) {
      cells.push({ date: new Date(month.getFullYear(), month.getMonth(), d) })
    }
    while (cells.length % 7 !== 0) cells.push({ date: null })
    return cells
  }, [month])

  const today = new Date()

  if (error) {
    return <Alert severity="error">Failed to load unavailability: {(error as Error).message}</Alert>
  }

  return (
    <Stack spacing={2} sx={{ width: '100%' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton
            size="small"
            onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}
          >
            <ChevronLeftIcon />
          </IconButton>
          <Typography variant="h6" sx={{ minWidth: 200, textAlign: 'center' }}>
            {month.toLocaleString(undefined, { month: 'long', year: 'numeric' })}
          </Typography>
          <IconButton
            size="small"
            onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}
          >
            <ChevronRightIcon />
          </IconButton>
          <Button size="small" onClick={() => setMonth(startOfMonth(new Date()))}>
            Today
          </Button>
        </Stack>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => {
            setForm(emptyForm())
            setFormOpen(true)
          }}
        >
          Add Unavailability
        </Button>
      </Stack>

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper variant="outlined">
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: 'action.hover',
            }}
          >
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
              <Box
                key={d}
                sx={{
                  px: 1,
                  py: 0.5,
                  textAlign: 'center',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  color: 'text.secondary',
                }}
              >
                {d}
              </Box>
            ))}
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
            {days.map((cell, idx) => {
              if (!cell.date) {
                return (
                  <Box
                    key={idx}
                    sx={{
                      minHeight: 72,
                      borderRight: idx % 7 < 6 ? 1 : 0,
                      borderBottom: 1,
                      borderColor: 'divider',
                      bgcolor: 'action.disabledBackground',
                    }}
                  />
                )
              }
              const matches = unavailabilities.filter((u) => affectsDay(u, cell.date as Date))
              const isToday = isSameDay(cell.date, today)
              return (
                <Tooltip
                  key={idx}
                  title={
                    matches.length === 0
                      ? ''
                      : matches.map(describeUnavailability).join('\n')
                  }
                >
                  <Box
                    sx={{
                      minHeight: 72,
                      p: 0.5,
                      borderRight: idx % 7 < 6 ? 1 : 0,
                      borderBottom: 1,
                      borderColor: 'divider',
                      bgcolor: matches.length > 0 ? 'warning.light' : undefined,
                      position: 'relative',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.25,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: isToday ? 700 : 500,
                        color: isToday ? 'primary.main' : 'text.primary',
                        textAlign: 'right',
                        pr: 0.5,
                      }}
                    >
                      {cell.date.getDate()}
                    </Typography>
                    {matches.slice(0, 2).map((m) => (
                      <Chip
                        key={m.id}
                        label={m.reason || m.unavailabilityType}
                        size="small"
                        color="warning"
                        sx={{ height: 16, fontSize: '0.65rem', justifyContent: 'flex-start' }}
                      />
                    ))}
                    {matches.length > 2 && (
                      <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                        +{matches.length - 2} more
                      </Typography>
                    )}
                  </Box>
                </Tooltip>
              )
            })}
          </Box>
        </Paper>
      )}

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Periods ({unavailabilities.length})
        </Typography>
        {unavailabilities.length === 0 ? (
          <Alert severity="info">No unavailability periods defined for this vehicle.</Alert>
        ) : (
          <Stack spacing={1}>
            {unavailabilities.map((u) => (
              <Paper
                key={u.id}
                variant="outlined"
                sx={{ p: 1.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25 }}>
                    <Chip
                      label={u.unavailabilityType.replace(/_/g, ' ')}
                      size="small"
                      variant="outlined"
                    />
                    {u.reason && (
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {u.reason}
                      </Typography>
                    )}
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {describeUnavailability(u)}
                  </Typography>
                </Box>
                <Tooltip title="Remove">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => deleteMutation.mutate(u.id)}
                    disabled={deleteMutation.isPending}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Paper>
            ))}
          </Stack>
        )}
      </Box>

      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Unavailability</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="ua-type">Type</InputLabel>
              <Select
                labelId="ua-type"
                label="Type"
                value={form.unavailabilityType}
                onChange={(e) =>
                  setForm({ ...emptyForm(), unavailabilityType: e.target.value as UnavailabilityType })
                }
              >
                {TYPE_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {form.unavailabilityType === 'ONE_TIME' && (
              <>
                <TextField
                  fullWidth
                  size="small"
                  type="datetime-local"
                  label="Start"
                  InputLabelProps={{ shrink: true }}
                  value={form.startDateTime?.slice(0, 16) ?? ''}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      startDateTime: e.target.value ? new Date(e.target.value).toISOString() : null,
                    })
                  }
                />
                <TextField
                  fullWidth
                  size="small"
                  type="datetime-local"
                  label="End"
                  InputLabelProps={{ shrink: true }}
                  value={form.endDateTime?.slice(0, 16) ?? ''}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      endDateTime: e.target.value ? new Date(e.target.value).toISOString() : null,
                    })
                  }
                />
              </>
            )}

            {form.unavailabilityType === 'DATE_RANGE' && (
              <>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Start Date"
                  InputLabelProps={{ shrink: true }}
                  value={form.startDate ?? ''}
                  onChange={(e) => setForm({ ...form, startDate: e.target.value || null })}
                />
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="End Date"
                  InputLabelProps={{ shrink: true }}
                  value={form.endDate ?? ''}
                  onChange={(e) => setForm({ ...form, endDate: e.target.value || null })}
                />
              </>
            )}

            {form.unavailabilityType === 'RECURRING_WEEKLY' && (
              <>
                <FormControl fullWidth size="small">
                  <InputLabel id="ua-dow">Day of Week</InputLabel>
                  <Select
                    labelId="ua-dow"
                    label="Day of Week"
                    value={form.dayOfWeek ?? ''}
                    onChange={(e) => setForm({ ...form, dayOfWeek: (e.target.value || null) as DayOfWeek | null })}
                  >
                    {DAY_OPTIONS.map((d) => (
                      <MenuItem key={d} value={d}>
                        {d}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  size="small"
                  type="time"
                  label="Start Time"
                  InputLabelProps={{ shrink: true }}
                  value={form.startTimeOfDay ?? ''}
                  onChange={(e) => setForm({ ...form, startTimeOfDay: e.target.value || null })}
                />
                <TextField
                  fullWidth
                  size="small"
                  type="time"
                  label="End Time"
                  InputLabelProps={{ shrink: true }}
                  value={form.endTimeOfDay ?? ''}
                  onChange={(e) => setForm({ ...form, endTimeOfDay: e.target.value || null })}
                />
              </>
            )}

            {form.unavailabilityType === 'RECURRING_DAILY' && (
              <>
                <TextField
                  fullWidth
                  size="small"
                  type="time"
                  label="Daily Start"
                  InputLabelProps={{ shrink: true }}
                  value={form.dailyStartTime ?? ''}
                  onChange={(e) => setForm({ ...form, dailyStartTime: e.target.value || null })}
                />
                <TextField
                  fullWidth
                  size="small"
                  type="time"
                  label="Daily End"
                  InputLabelProps={{ shrink: true }}
                  value={form.dailyEndTime ?? ''}
                  onChange={(e) => setForm({ ...form, dailyEndTime: e.target.value || null })}
                />
              </>
            )}

            <TextField
              fullWidth
              size="small"
              label="Reason"
              value={form.reason ?? ''}
              onChange={(e) => setForm({ ...form, reason: e.target.value || null })}
              multiline
              minRows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={addMutation.isPending}
            onClick={() => addMutation.mutate(form)}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}
